/**
 * Auth store — access + refresh tokens persisted in localStorage under "th_auth".
 * Pure storage layer (no React, no network): api.ts reads tokens from here, AuthContext
 * owns the network calls. Tenancy (orgId/role) is NEVER stored or sent from the client —
 * the server derives it from the JWT. We only keep the persona for client-side routing.
 */
import type { Persona } from "@talenthop/shared";

const KEY = "th_auth";
const PERSONAS: readonly Persona[] = ["admin", "user", "candidate"];

export interface Session {
  accessToken: string;
  refreshToken: string;
  /** always "Bearer" from the API. */
  tokenType: string;
  /** used only for client-side routing; authorization is enforced server-side. */
  persona: Persona;
}

function isPersona(v: unknown): v is Persona {
  return typeof v === "string" && (PERSONAS as readonly string[]).includes(v);
}

/** Decode the `persona` claim from a JWT without verifying it (routing hint only). */
export function personaFromToken(accessToken: string): Persona | null {
  try {
    const payload = accessToken.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { persona?: unknown };
    return isPersona(claims.persona) ? claims.persona : null;
  } catch {
    return null;
  }
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (typeof parsed.accessToken !== "string" || typeof parsed.refreshToken !== "string") return null;
    const persona = isPersona(parsed.persona)
      ? parsed.persona
      : personaFromToken(parsed.accessToken);
    if (!persona) return null;
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      tokenType: typeof parsed.tokenType === "string" ? parsed.tokenType : "Bearer",
      persona,
    };
  } catch {
    return null;
  }
}

export function setSession(session: Session): void {
  localStorage.setItem(KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(KEY);
}

export function getToken(): string | null {
  return getSession()?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return getSession()?.refreshToken ?? null;
}

export function isAuthed(): boolean {
  return getSession() !== null;
}

export function getPersona(): Persona | null {
  return getSession()?.persona ?? null;
}
